import React from "react";

export default function PayslipBreakdown({ payslip }) {
  if (!payslip) return null;

  const earnings = [...(payslip.earnings || [])];
  if ((payslip.overtimePay || 0) > 0 && !earnings.some((x) => x.name === "Overtime Pay")) {
    earnings.push({ name: "Overtime Pay", amount: payslip.overtimePay });
  }
  if ((payslip.shiftAllowance || 0) > 0 && !earnings.some((x) => x.name === "Shift Allowance")) {
    earnings.push({ name: "Shift Allowance", amount: payslip.shiftAllowance });
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-neutral-200 mb-2">Earnings</h3>
          <ul className="divide-y divide-gray-100 border rounded-lg">
            {earnings.map((e, i) => (
              <li key={i} className="flex justify-between px-3 py-2 text-sm">
                <span>{e.name}</span>
                <span className="font-medium">₹{Number(e.amount).toLocaleString()}</span>
              </li>
            ))}
            <li className="flex justify-between px-3 py-2 text-sm font-semibold bg-gray-50 dark:bg-neutral-950">
              <span>Gross</span>
              <span>₹{Number(payslip.grossPay || 0).toLocaleString()}</span>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-neutral-200 mb-2">Deductions</h3>
          <ul className="divide-y divide-gray-100 border rounded-lg">
            {(payslip.deductions || []).map((d, i) => (
              <li key={i} className="flex justify-between px-3 py-2 text-sm">
                <span>{d.name}</span>
                <span className="font-medium">₹{Number(d.amount).toLocaleString()}</span>
              </li>
            ))}
            {(payslip.lopDeduction || 0) > 0 && (
              <li className="flex justify-between px-3 py-2 text-sm text-red-700">
                <span>LOP deduction</span>
                <span className="font-medium">₹{Number(payslip.lopDeduction).toLocaleString()}</span>
              </li>
            )}
            {(payslip.halfDayDeduction || 0) > 0 && (
              <li className="flex justify-between px-3 py-2 text-sm text-red-700">
                <span>Half-day deduction</span>
                <span className="font-medium">₹{Number(payslip.halfDayDeduction).toLocaleString()}</span>
              </li>
            )}
            {(payslip.lateDeduction || 0) > 0 && (
              <li className="flex justify-between px-3 py-2 text-sm text-red-700">
                <span>Late penalty</span>
                <span className="font-medium">₹{Number(payslip.lateDeduction).toLocaleString()}</span>
              </li>
            )}
            <li className="flex justify-between px-3 py-2 text-sm font-semibold bg-gray-50 dark:bg-neutral-950">
              <span>Total Deductions</span>
              <span>₹{Number(payslip.totalDeductions || 0).toLocaleString()}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-neutral-950 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
        <div>
          <p className="text-gray-500 dark:text-neutral-400">Days in month</p>
          <p className="font-semibold">{payslip.totalDaysInMonth}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">Present</p>
          <p className="font-semibold">{payslip.presentDays ?? "-"}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">LOP days</p>
          <p className="font-semibold">{payslip.lopDays || 0}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">Half days</p>
          <p className="font-semibold">{payslip.halfDays || 0}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">Late days</p>
          <p className="font-semibold">{payslip.lateDays || 0}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">Per day rate</p>
          <p className="font-semibold">₹{Number(payslip.perDayRate || 0).toLocaleString()}</p>
        </div>
      </div>

      <div className="border rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
        <div>
          <p className="text-gray-500 dark:text-neutral-400">PF (Emp)</p>
          <p className="font-semibold">₹{Number(payslip.pfEmployee || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">PF (Er)</p>
          <p className="font-semibold">₹{Number(payslip.pfEmployer || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">ESI (Emp)</p>
          <p className="font-semibold">₹{Number(payslip.esiEmployee || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">ESI (Er)</p>
          <p className="font-semibold">₹{Number(payslip.esiEmployer || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">Prof. Tax</p>
          <p className="font-semibold">₹{Number(payslip.professionalTax || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-neutral-400">TDS</p>
          <p className="font-semibold">₹{Number(payslip.tds || 0).toLocaleString()}</p>
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-indigo-50 flex justify-between items-center">
        <span className="text-lg font-semibold text-indigo-900">Net Pay</span>
        <span className="text-2xl font-bold text-indigo-700">
          ₹{Number(payslip.netPay || 0).toLocaleString()}
        </span>
      </div>
    </div>
  );
}
